<template>
  <div class="p-m-3">
    <div v-if="isAdmin">
      <h2>Rebates</h2>
      <TabView>
        <!-- Tab 1: Settings -->
        <TabPanel header="Settings">
          <div class="p-card p-p-3 p-mb-3">
            <h4>Rebate notice</h4>
            <p>The rebate &amp; customer message is always shown as a banner when a customer is selected. Tick the box below to also show it as a popup window. This is the default &mdash; individual customers can override it.</p>
            <div class="p-field-checkbox" style="margin-bottom: 0;">
              <Checkbox id="globalPopup" v-model="globalPopup" :binary="true" @change="saveGlobalPopup" />
              <label for="globalPopup">Show Rebate &amp; Customer message as a Popup Window</label>
            </div>
          </div>
          <div class="p-card p-p-3 p-mb-3">
            <h4>Default rebate message</h4>
            <p>Shown when a customer has a rebate but no custom Rebate Message of their own.</p>
            <div class="p-d-flex p-ai-center p-flex-wrap" style="gap: 8px;">
              <InputText v-model="defaultMessage" style="width: 34rem; max-width: 100%;" />
              <Button label="Save message" icon="pi pi-check" class="p-button-success" @click="saveDefaultMessage" />
            </div>
            <small style="display:block; margin-top:6px; color:#6c757d;">
              Use <code>{percent}</code> where the customer's rebate percentage should appear,
              e.g. "This customer receives a {percent}% rebate".
            </small>
          </div>
        </TabPanel>

        <!-- Tab 2: Batch update -->
        <TabPanel header="Batch Update">
          <div class="p-card p-p-3 p-mb-3">
            <h4>Find customers</h4>
            <p>Apply a filter to load customers, then use the panel below to update them.</p>
            <div class="p-d-flex p-ai-center p-flex-wrap" style="gap: 8px; margin-bottom: 10px;">
              <Button :label="filterButtonLabel" icon="pi pi-filter" class="p-button-info" @click="openFilters" />
              <span class="p-input-icon-left">
                <i class="pi pi-search" />
                <InputText v-model="filter.searchText" placeholder="Name / code" @keyup.enter="onSearch" />
              </span>
              <Button label="Search" icon="pi pi-search" class="p-button-info" @click="onSearch" />
              <span class="p-ml-2" v-if="hasLoaded">{{ recCount }} matching</span>
            </div>
          </div>

          <!-- Batch apply panel: only once the grid is populated, positioned ABOVE the datatable -->
          <div class="p-card p-p-3 p-mb-3" v-if="hasLoaded && customers.length">
            <h4>Batch apply</h4>
            <p>Applies to the {{ resolvedIds.length }} selected customer(s). Tick a field to change it; leave a ticked field empty to clear it.</p>
            <div class="p-d-flex p-flex-wrap p-ai-center" style="gap: 3.5rem; margin-bottom: 10px;">
              <div class="p-field-checkbox" style="margin-bottom: 0;">
                <Checkbox id="applyRebate" v-model="apply.rebate" :binary="true" />
                <label for="applyRebate" class="p-mr-2">Rebate %</label>
                <input type="number" step="0.01" min="0" max="100" inputmode="decimal" class="p-inputtext p-component" style="width: 9rem;" :disabled="!apply.rebate" :value="rebateValue" @input="rebateValue = $event.target.value === '' ? null : parseFloat($event.target.value)" />
              </div>
              <div class="p-field-checkbox" style="margin-bottom: 0;">
                <Checkbox id="applyMode" v-model="apply.displayMode" :binary="true" />
                <label for="applyMode" class="p-mr-2">Show as popup</label>
                <Dropdown v-model="displayModeValue" :disabled="!apply.displayMode" :options="rebateDisplayModeOptions" optionLabel="label" optionValue="value" style="width: 13rem;" />
              </div>
            </div>
            <div class="p-field-checkbox" style="margin-bottom: 2px;">
              <Checkbox id="applyMsg" v-model="apply.message" :binary="true" />
              <label for="applyMsg" class="p-mr-2">Rebate Message</label>
              <InputText v-model="messageValue" :disabled="!apply.message" style="width: 34rem; max-width: 100%;" />
            </div>
            <small style="display: block; margin: 0 0 12px 1.9rem; color: #6c757d;">
              Select this to override the standard rebate message and set a custom one (leave empty to clear it).
            </small>
            <Button :label="'Select all ' + recCount + ' matching'" icon="pi pi-check-square" class="p-button-secondary p-mr-2" @click="selectAllMatching" :disabled="!recCount" />
            <Button :label="'Apply to ' + resolvedIds.length + ' customer(s)'" icon="pi pi-check" class="p-button-success" @click="confirmBatch" :disabled="!canApply" />
          </div>

          <!-- Grid -->
          <div class="p-card p-p-3 p-mb-3">
            <DataTable
              :value="customers"
              :lazy="true"
              :paginator="true"
              :rows="pageSize"
              :totalRecords="recCount"
              :first="(pageNo - 1) * pageSize"
              :loading="loading"
              dataKey="customerId"
              :selection.sync="selectedRows"
              @page="onPage"
              class="p-mt-2"
            >
              <template #empty>
                {{ hasLoaded ? 'No customers match this filter.' : 'Apply a filter to load customers.' }}
              </template>
              <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
              <Column field="custCode" header="Code"></Column>
              <Column field="custName" header="Name"></Column>
              <Column field="branchCode" header="Branch"></Column>
              <Column field="customerType" header="Type"></Column>
              <Column field="industryName" header="Industry"></Column>
              <Column field="regionName" header="Region"></Column>
              <Column field="salesExecName" header="Sales exec"></Column>
              <Column field="rebatePercent" header="Rebate %"></Column>
              <Column field="rebateMessage" header="Rebate Message"></Column>
              <Column header="Show as popup">
                <template #body="s">{{ displayModeLabel(s.data.msgDisplayMode) }}</template>
              </Column>
            </DataTable>
          </div>
        </TabPanel>
      </TabView>
    </div>
    <div v-else>
      <h3>Not authorised</h3>
    </div>

    <!-- Filter customers popup (mirrors the Customers view filter dialog) -->
    <Dialog :visible.sync="filterDialogVisible" :modal="true" header="Filter customers" :style="{ width: '480px' }">
      <div class="p-fluid" style="padding-top: 4px;">
        <div class="p-field">
          <label>Customer types</label>
          <MultiSelect v-model="filter.typeIds" :options="opts.types" optionLabel="typeName" optionValue="id" display="chip" :filter="true" placeholder="Any" />
        </div>
        <div class="p-field">
          <label>Regions</label>
          <MultiSelect v-model="filter.regionIds" :options="opts.regions" optionLabel="regionName" optionValue="id" display="chip" :filter="true" placeholder="Any" />
        </div>
        <div class="p-field">
          <label>Industries</label>
          <MultiSelect v-model="filter.industryIds" :options="opts.industries" optionLabel="industryName" optionValue="id" display="chip" :filter="true" placeholder="Any" />
        </div>
        <div class="p-field">
          <label>Branches</label>
          <MultiSelect v-model="filter.branchIds" :options="opts.branches" optionLabel="branchName" optionValue="id" display="chip" :filter="true" placeholder="Any" />
        </div>
        <div class="p-field">
          <label>Sales execs</label>
          <MultiSelect v-model="filter.salesExecIds" :options="opts.salesExecs" optionLabel="salesExecName" optionValue="id" display="chip" :filter="true" placeholder="Any" />
        </div>
        <div class="p-field">
          <label>Search (name / code)</label>
          <InputText v-model="filter.searchText" placeholder="Any" />
        </div>
      </div>
      <template #footer>
        <Button label="Clear All Filters" icon="pi pi-times" class="p-button-danger" @click="clearAllFilters" />
        <Button label="Cancel" icon="pi pi-times" class="p-button-info" @click="cancelFilters" />
        <Button label="Apply Filters" icon="pi pi-check" class="p-button-success" @click="applyFilters" />
      </template>
    </Dialog>

    <Dialog :visible.sync="confirmVisible" :modal="true" header="Confirm batch update">
      <p>This will update {{ resolvedIds.length }} customer(s). Continue?</p>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times" class="p-button-info" @click="confirmVisible = false" />
        <Button label="Apply" icon="pi pi-check" class="p-button-success" @click="runBatch" :disabled="batchRunning" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import AppSettingsService from '@/service/AppSettingsService'
import RebatesService from '@/service/RebatesService'
import CustomerService from '@/service/CustomerService'
import { buildBatchUpdateBody } from '@/service/rebateBatchBody'

export default {
  name: 'Rebates',
  data() {
    return {
      appSettingsService: new AppSettingsService(),
      rebatesService: new RebatesService(),
      customerService: new CustomerService(),
      globalPopup: false,
      defaultMessage: '',
      hasLoaded: false,
      loading: false,
      customers: [],
      recCount: 0,
      pageNo: 1,
      pageSize: 50,
      selectedRows: [],
      filter: { typeIds: [], regionIds: [], industryIds: [], branchIds: [], salesExecIds: [], searchText: '' },
      opts: { types: [], regions: [], industries: [], branches: [], salesExecs: [] },
      rebateDisplayModeOptions: [
        { label: 'Use default', value: 1 },
        { label: 'Yes — show as popup', value: 3 },
        { label: 'No — banner only', value: 2 }
      ],
      apply: { rebate: false, message: false, displayMode: false },
      rebateValue: null,
      messageValue: '',
      displayModeValue: 1,
      selectAllIds: null,   // non-null when "select all matching" was used
      confirmVisible: false,
      filterDialogVisible: false,
      filterBackup: null,   // snapshot of filters when the popup opens, for Cancel
      batchRunning: false
    }
  },
  computed: {
    isAdmin() {
      const p = this.$store.getters['auth/getMyProfile']
      return !!(p && p.role && p.role.showAdmin)
    },
    resolvedIds() {
      if (this.selectAllIds) return this.selectAllIds
      return (this.selectedRows || []).map(r => r.customerId)
    },
    canApply() {
      return this.resolvedIds.length > 0 &&
        (this.apply.rebate || this.apply.message || this.apply.displayMode)
    },
    activeFilterCount() {
      const f = this.filter
      let n = 0
      if (f.typeIds.length) n++
      if (f.regionIds.length) n++
      if (f.industryIds.length) n++
      if (f.branchIds.length) n++
      if (f.salesExecIds.length) n++
      if (f.searchText) n++
      return n
    },
    filterButtonLabel() {
      return this.activeFilterCount ? ('Filters (' + this.activeFilterCount + ')') : 'Filters'
    }
  },
  watch: {
    selectedRows() {
      // any selection change (including the header select-all/unselect-all checkbox,
      // which does not emit row-select/row-unselect) invalidates a prior "select all matching" set
      this.selectAllIds = null
    }
  },
  mounted() {
    if (this.isAdmin) {
      this.loadGlobalPopup()
      this.loadDefaultMessage()
      this.loadFilterOptions()
    }
  },
  methods: {
    async loadGlobalPopup() {
      try {
        const res = await this.appSettingsService.getSetting('rebateDisplayMode')
        if (res && res.settingValue) this.globalPopup = (res.settingValue === 'popup')
      } catch (e) { /* keep default */ }
    },
    async saveGlobalPopup() {
      try {
        await this.appSettingsService.updateSetting('rebateDisplayMode', this.globalPopup ? 'popup' : 'label')
        this.$toast.add({ severity: 'success', summary: 'Saved', detail: 'Rebate popup setting updated', life: 3000 })
      } catch (err) {
        const msg = (err && err.response && err.response.data) ? err.response.data : 'Could not save setting'
        this.$toast.add({ severity: 'error', summary: 'Save failed', detail: msg, life: 5000 })
        this.loadGlobalPopup()
      }
    },
    async loadDefaultMessage() {
      try {
        const res = await this.appSettingsService.getSetting('rebateDefaultMessage')
        if (res && res.settingValue) this.defaultMessage = res.settingValue
      } catch (e) { /* keep default */ }
    },
    async saveDefaultMessage() {
      try {
        await this.appSettingsService.updateSetting('rebateDefaultMessage', this.defaultMessage || '')
        this.$toast.add({ severity: 'success', summary: 'Saved', detail: 'Default rebate message updated', life: 3000 })
      } catch (err) {
        const msg = (err && err.response && err.response.data) ? err.response.data : 'Could not save message'
        this.$toast.add({ severity: 'error', summary: 'Save failed', detail: msg, life: 5000 })
      }
    },
    displayModeLabel(mode) {
      if (mode === 2) return 'No (banner only)'
      if (mode === 3) return 'Yes'
      return 'Default'
    },
    buildFilterBody(pageNo, pageSize) {
      return {
        typeIds: this.filter.typeIds,
        regionIds: this.filter.regionIds,
        industryIds: this.filter.industryIds,
        branchIds: this.filter.branchIds,
        salesExecIds: this.filter.salesExecIds,
        searchText: this.filter.searchText,
        pageNo: pageNo,
        pageSize: pageSize
      }
    },
    async loadCustomers() {
      this.loading = true
      try {
        const res = await this.rebatesService.getCustomers(this.buildFilterBody(this.pageNo, this.pageSize))
        this.customers = (res && res.customers) || []
        this.recCount = (res && res.recCount) || 0
        this.hasLoaded = true
      } catch (err) {
        const msg = (err && err.response && err.response.data) ? err.response.data : 'Could not load customers'
        this.$toast.add({ severity: 'error', summary: 'Load failed', detail: msg, life: 5000 })
      } finally {
        this.loading = false
      }
    },
    onSearch() {
      this.pageNo = 1
      this.selectedRows = []
      this.selectAllIds = null
      this.loadCustomers()
    },
    onPage(event) {
      this.pageNo = Math.floor(event.first / event.rows) + 1
      this.pageSize = event.rows
      this.loadCustomers()
    },
    openFilters() {
      this.filterBackup = JSON.parse(JSON.stringify(this.filter))
      this.filterDialogVisible = true
    },
    cancelFilters() {
      if (this.filterBackup) this.filter = this.filterBackup
      this.filterBackup = null
      this.filterDialogVisible = false
    },
    clearAllFilters() {
      this.filter = { typeIds: [], regionIds: [], industryIds: [], branchIds: [], salesExecIds: [], searchText: '' }
    },
    applyFilters() {
      this.filterBackup = null
      this.filterDialogVisible = false
      this.onSearch()
    },
    async loadFilterOptions() {
      try { this.opts.types = await this.customerService.getAllActiveCustomerTypes() } catch (e) {}
      try { this.opts.regions = await this.customerService.getAllActiveRegions() } catch (e) {}
      try { this.opts.industries = await this.customerService.getAllActiveIndustryTypes() } catch (e) {}
      try { this.opts.branches = await this.customerService.getAllActiveMBPBranches() } catch (e) {}
      try { this.opts.salesExecs = await this.customerService.getAllActiveSalesExecs() } catch (e) {}
    },
    buildFilterContext() {
      const parts = []
      if (this.filter.typeIds.length) parts.push('types=' + this.filter.typeIds.length)
      if (this.filter.regionIds.length) parts.push('regions=' + this.filter.regionIds.length)
      if (this.filter.industryIds.length) parts.push('industries=' + this.filter.industryIds.length)
      if (this.filter.branchIds.length) parts.push('branches=' + this.filter.branchIds.length)
      if (this.filter.salesExecIds.length) parts.push('salesExecs=' + this.filter.salesExecIds.length)
      if (this.filter.searchText) parts.push('search=' + this.filter.searchText)
      return parts.join('; ')
    },
    async selectAllMatching() {
      // fetch all matching ids in one large page
      try {
        const res = await this.rebatesService.getCustomers(this.buildFilterBody(1, this.recCount || 100000))
        this.selectAllIds = ((res && res.customers) || []).map(c => c.customerId)
        this.$toast.add({ severity: 'info', summary: 'Selected', detail: this.selectAllIds.length + ' customers selected', life: 3000 })
      } catch (err) {
        const msg = (err && err.response && err.response.data) ? err.response.data : 'Could not select all'
        this.$toast.add({ severity: 'error', summary: 'Failed', detail: msg, life: 5000 })
      }
    },
    confirmBatch() {
      if (!this.canApply) return
      this.confirmVisible = true
    },
    async runBatch() {
      this.batchRunning = true
      const body = buildBatchUpdateBody({
        customerIds: this.resolvedIds,
        rebate: { apply: this.apply.rebate, value: this.rebateValue },
        message: { apply: this.apply.message, value: this.messageValue === '' ? null : this.messageValue },
        displayMode: { apply: this.apply.displayMode, value: this.displayModeValue },
        filterContext: this.buildFilterContext()
      })
      try {
        const res = await this.rebatesService.batchUpdate(body)
        this.$toast.add({ severity: 'success', summary: 'Updated', detail: (res.updatedCount || 0) + ' customers updated', life: 4000 })
        this.confirmVisible = false
        this.selectAllIds = null
        this.selectedRows = []
        this.loadCustomers()
      } catch (err) {
        const msg = (err && err.response && err.response.data) ? err.response.data : 'Batch update failed'
        this.$toast.add({ severity: 'error', summary: 'Update failed', detail: msg, life: 6000 })
      } finally {
        this.batchRunning = false
      }
    }
  }
}
</script>
